import { useState, useCallback, useMemo, useRef } from 'react';
import { listVaults, assignAgentToVault } from '../../../../../../../../services/vault';
import { listDocuments, assignAgentToDocument } from '../../../../../../../../services/document';
import { useAgent } from '../../../../../context';
import { useDrives } from '../../../../../context';
import { useStore } from '../../../../../../../../hooks';

/**
 * Hook para manejar la lógica del Slideout de Drives
 * Maneja: lista de drives, documentos de un drive, selecciones, y asignación al agente
 * 
 * ✅ OPTIMIZACIONES DE RENDIMIENTO:
 * - Caché de documentos por drive para evitar peticiones redundantes
 * - Caché de drives con duración de 5 minutos
 * - No limpiar documentos al volver atrás (preservar en caché)
 * - Preservar estado al cerrar para próxima apertura
 */
const useSlideoutDrives = () => {
  const { agentId } = useAgent();
  const { fetchAll: refreshAgentDrives } = useDrives();
  const { authStore } = useStore();

  // ✅ Caché de documentos por drive (Map<vaultUuid, documents[]>)
  const documentsCacheRef = useRef(new Map());
  
  // ✅ Flags para control de caché de drives
  const drivesFetchedRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  // Estado de drives disponibles
  const [drives, setDrives] = useState([]);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drivesError, setDrivesError] = useState(null);

  // Estado de documentos del drive seleccionado
  const [driveDocuments, setDriveDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);

  // Estado de navegación
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [isDocumentsView, setIsDocumentsView] = useState(false);

  // Estado de selecciones
  const [selectedDriveIds, setSelectedDriveIds] = useState(new Set());
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(new Set());

  // Estado de búsqueda
  const [searchQuery, setSearchQuery] = useState('');

  // Estado de asignación
  const [isAssigning, setIsAssigning] = useState(false);

  // ✅ OPTIMIZADO: Fetch drives con caché
  const fetchDrives = useCallback(async (force = false) => {
    // Verificar si ya hay datos en caché y no es necesario refrescar
    const now = Date.now();
    const shouldFetch = force || 
                       !drivesFetchedRef.current || 
                       (now - lastFetchTimeRef.current) > CACHE_DURATION ||
                       drives.length === 0;

    if (!shouldFetch) {
      // Usar datos en caché - no hacer petición
      return;
    }

    setDrivesLoading(true);
    setDrivesError(null);

    try {
      const response = await listVaults({
        tenant_id: authStore?.selectedAccount?.id || '1',
        page: 1,
        page_size: 100,
        order_by: 'created_at',
        order_direction: 'desc',
        scope: "USER",
      });

      // La respuesta tiene la estructura: { msgCode, data: [...], meta: {...} }
      const drivesList = response.data?.data || response.data?.body || [];
      // Asegurar que siempre sea un array
      const safeDrivesList = Array.isArray(drivesList) ? drivesList : [];
      setDrives(safeDrivesList);
      
      // ✅ Marcar como cargado y actualizar timestamp
      drivesFetchedRef.current = true;
      lastFetchTimeRef.current = now;
    } catch (error) {
      console.error('Error fetching drives:', error);
      setDrivesError(error);
      setDrives([]); // Asegurar array vacío en caso de error
    } finally {
      setDrivesLoading(false);
    }
  }, [authStore, drives.length]);

  // ✅ OPTIMIZADO: Fetch documentos con caché
  const fetchDriveDocuments = useCallback(async (drive, force = false) => {
    const vaultUuid = drive.vaultUuid || drive.uuid;
    if (!vaultUuid) return;

    // ✅ Verificar caché primero
    const cachedDocuments = documentsCacheRef.current.get(vaultUuid);
    if (cachedDocuments && !force) {
      // Usar datos en caché - respuesta instantánea
      setSelectedDrive(drive);
      setIsDocumentsView(true);
      setDriveDocuments(cachedDocuments);
      return;
    }

    setSelectedDrive(drive);
    setIsDocumentsView(true);
    setDocumentsLoading(true);
    setDocumentsError(null);

    try {
      const response = await listDocuments({
        vault_uuid: vaultUuid,
        page_size: 100,
      });

      // La respuesta tiene estructura: { body: [...] }
      const rawDocuments = response.data?.body || [];
      
      // Normalizar documentos: mapear documentUuid a uuid y otros campos
      const documentsList = rawDocuments.map((doc) => {
        const fileName = doc.name || '';
        const extractedExtension = doc.extension || (fileName.includes('.') 
          ? fileName.split('.').pop().toLowerCase() 
          : null);
        
        const getTypeFromExtension = (ext) => {
          if (!ext) return null;
          const extLower = ext.toLowerCase();
          if (['pdf'].includes(extLower)) return 'application/pdf';
          if (['doc', 'docx'].includes(extLower)) return 'application/msword';
          if (['xls', 'xlsx'].includes(extLower)) return 'application/vnd.ms-excel';
          if (['ppt', 'pptx'].includes(extLower)) return 'application/vnd.ms-powerpoint';
          if (['jpg', 'jpeg', 'png', 'gif'].includes(extLower)) return 'image';
          return null;
        };
        
        return {
          ...doc,
          uuid: doc.documentUuid || doc.uuid || doc.id,
          id: doc.documentUuid || doc.uuid || doc.id,
          created_at: doc.createdAt || doc.created_at,
          createdAt: doc.createdAt || doc.created_at,
          updated_at: doc.updatedAt || doc.updated_at,
          updatedAt: doc.updatedAt || doc.updated_at,
          owner: doc.ownerFullName || doc.owner || doc.createdBy || 'Unknown',
          createdBy: doc.ownerFullName || doc.owner || doc.createdBy || 'Unknown',
          extension: extractedExtension,
          type: doc.type || getTypeFromExtension(extractedExtension),
        };
      });
      
      // Asegurar que siempre sea un array
      const safeDocumentsList = Array.isArray(documentsList) ? documentsList : [];
      
      // ✅ Guardar en caché para próxima vez
      documentsCacheRef.current.set(vaultUuid, safeDocumentsList);
      setDriveDocuments(safeDocumentsList);
    } catch (error) {
      console.error('Error fetching drive documents:', error);
      setDocumentsError(error);
      setDriveDocuments([]); // Asegurar array vacío en caso de error
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  // ✅ OPTIMIZADO: Volver atrás sin limpiar documentos (preservar en caché)
  const handleBackToList = useCallback(() => {
    setSelectedDrive(null);
    setIsDocumentsView(false);
    // ✅ NO limpiar driveDocuments - mantener en caché para respuesta instantánea
    // setDriveDocuments([]); // ❌ REMOVIDO - los documentos ya están en caché
    setSearchQuery('');
  }, []);

  // Toggle selección de drive
  const toggleDriveSelection = useCallback((driveId) => {
    setSelectedDriveIds((prev) => {
      const next = new Set(prev);
      if (next.has(driveId)) {
        next.delete(driveId);
      } else {
        next.add(driveId);
      }
      return next;
    });
  }, []);

  // Toggle selección de documento
  const toggleDocumentSelection = useCallback((documentId) => {
    setSelectedDocumentIds((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  // Limpiar selecciones
  const clearSelections = useCallback(() => {
    setSelectedDriveIds(new Set());
    setSelectedDocumentIds(new Set());
  }, []);

  // Grant access - asignar drives y documentos al agente
  // Acepta selecciones como parámetros opcionales para preservar estado
  const grantAccess = useCallback(async (driveIdsToAssign, documentIdsToAssign) => {
    if (!agentId) return { success: false, error: 'No agent ID' };

    // Usar los parámetros si se proporcionan, sino usar el estado actual
    const drivesToAssign = driveIdsToAssign || selectedDriveIds;
    const docsToAssign = documentIdsToAssign || selectedDocumentIds;

    setIsAssigning(true);
    let successCount = 0;
    const errors = [];
    const userId = authStore?.userId || 'user123';
    const tenantId = authStore?.selectedAccount?.id || '1';

    // Asignar drives
    for (const driveId of drivesToAssign) {
      try {
        await assignAgentToVault(
          driveId,
          agentId,
          'READER',
          'Agent assigned to vault via Knowledge Page'
        );
        successCount++;
      } catch (error) {
        console.error('Error assigning drive:', driveId, error);
        errors.push(`Drive: ${error.message || 'Failed'}`);
      }
    }

    // Asignar documentos
    for (const documentId of docsToAssign) {
      try {
        const body = {
          document_uuid: documentId,
          agent_uuid: agentId,
          expires_at: '2026-12-31 23:59:59',
          owner: userId,
          created_by: userId,
          granted_by: userId,
          // description: 'Assign agent to document',
          privilege: 'READER',
        };
        await assignAgentToDocument(body);
        successCount++;
      } catch (error) {
        console.error('Error assigning document:', documentId, error);
        errors.push(`Document: ${error.message || 'Failed'}`);
      }
    }

    setIsAssigning(false);
    // NO limpiar selecciones aquí - se limpiarán desde el componente después de confirmar

    // Refrescar la lista de drives/documentos del agente
    refreshAgentDrives(true);

    return {
      success: errors.length === 0,
      successCount,
      errors,
    };
  }, [agentId, authStore, selectedDriveIds, selectedDocumentIds, refreshAgentDrives]);

  // Filtrar drives por búsqueda
  const filteredDrives = useMemo(() => {
    // Validación defensiva: asegurar que drives sea un array
    if (!Array.isArray(drives)) return [];
    if (!searchQuery) return drives;
    const query = searchQuery.toLowerCase();
    return drives.filter(
      (drive) =>
        drive.name?.toLowerCase().includes(query) ||
        drive.description?.toLowerCase().includes(query)
    );
  }, [drives, searchQuery]);

  // Filtrar documentos por búsqueda
  const filteredDocuments = useMemo(() => {
    // Validación defensiva: asegurar que driveDocuments sea un array
    if (!Array.isArray(driveDocuments)) return [];
    if (!searchQuery) return driveDocuments;
    const query = searchQuery.toLowerCase();
    return driveDocuments.filter(
      (doc) =>
        doc.name?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query)
    );
  }, [driveDocuments, searchQuery]);

  // Contadores
  const selectedCount = selectedDriveIds.size + selectedDocumentIds.size;
  const selectedDriveTitle = selectedDrive?.name || 'Add from Drives';

  // Items seleccionados para el modal de confirmación
  // Solo calcular cuando realmente se necesita (cuando hay selecciones)
  const selectedItems = useMemo(() => {
    // Optimización temprana: si no hay selecciones, retornar array vacío inmediatamente
    if (selectedDriveIds.size === 0 && selectedDocumentIds.size === 0) {
      return [];
    }
    
    // Validación defensiva
    const safeDrives = Array.isArray(drives) ? drives : [];
    const safeDriveDocuments = Array.isArray(driveDocuments) ? driveDocuments : [];
    
    const driveItems = safeDrives
      .filter((d) => selectedDriveIds.has(d.vaultUuid || d.uuid))
      .map((d) => ({ ...d, type: 'drive', icon: 'folder' }));

    const docItems = safeDriveDocuments
      .filter((d) => selectedDocumentIds.has(d.uuid || d.id))
      .map((d) => ({ ...d, type: 'document', icon: 'description' }));

    return [...driveItems, ...docItems];
  }, [drives, driveDocuments, selectedDriveIds, selectedDocumentIds]);

  // ✅ OPTIMIZADO: Reset solo UI state, preservar caché
  const resetState = useCallback(() => {
    setSelectedDrive(null);
    setIsDocumentsView(false);
    // ✅ NO limpiar documentos del drive actual (ya están en caché)
    // setDriveDocuments([]); // ❌ REMOVIDO - preservar para próxima apertura
    setSearchQuery('');
    clearSelections();
    // ✅ NO limpiar drives ni caché de documentos - preservar para próxima apertura
  }, [clearSelections]);

  // ✅ NUEVO: Función para limpiar caché manualmente si es necesario
  const clearCache = useCallback(() => {
    documentsCacheRef.current.clear();
    drivesFetchedRef.current = false;
    lastFetchTimeRef.current = 0;
    setDrives([]);
    setDriveDocuments([]);
  }, []);

  return {
    // Drives
    drives: filteredDrives,
    drivesLoading,
    drivesError,
    fetchDrives,

    // Documentos del drive
    driveDocuments: filteredDocuments,
    documentsLoading,
    documentsError,
    fetchDriveDocuments,

    // Navegación
    selectedDrive,
    isDocumentsView,
    handleBackToList,
    selectedDriveTitle,

    // Selecciones
    selectedDriveIds,
    selectedDocumentIds,
    toggleDriveSelection,
    toggleDocumentSelection,
    clearSelections,
    selectedCount,
    selectedItems,

    // Búsqueda
    searchQuery,
    setSearchQuery,

    // Asignación
    grantAccess,
    isAssigning,

    // Reset
    resetState,
    
    // ✅ NUEVO: Función para limpiar caché
    clearCache,
  };
};

export default useSlideoutDrives;

